<script setup>
import { computed, inject, nextTick, onMounted, provide, ref, watch } from "vue";

import ActionButton from "@/Components/Modal/ActionButton.vue";
import Modal from "@/Components/Modal/Modal.vue";

const emit = defineEmits(["close", "canceled", "confirmed", "before-open", "after-open", "before-close", "after-close"]);

const props = defineProps({
  show: {
    type: Boolean,
    required: false
  },
  actions: {
    type: Array,
    required: false,
    default: () => [],
    validator: (actions) =>
      actions.every((action) => typeof action.label === "string" && typeof action.action === "function" && typeof action.enable === "boolean")
  },
  autoClose: {
    type: Boolean,
    required: false,
    default: false
  },
  validate: {
    type: Function,
    required: false,
    default: () => true
  },
  wrapperClass: {
    type: String,
    required: false,
    default: ""
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false
  },
  is: {
    type: String,
    required: false,
    default: "span"
  },
  buttonIs: {
    type: String,
    required: false,
    default: "span"
  },
  maxWidth: {
    type: String,
    default: "sm"
  }
});

const visible = ref(props.show);

const open = () => {
  if (!props.disabled) {
    emit("before-open");
    visible.value = true;

    nextTick(() => {
      emit("after-open");
    });
  }
};

const close = () => {
  emit("before-close");
  visible.value = false;
  emit("close");

  nextTick(() => {
    emit("after-close");
  });
};

onMounted(() => {
  if (props.show) {
    open();
  }
});

watch(
  () => props.show,
  (show) => {
    if (show) {
      open();
    } else {
      close();
    }
  },
  { immediate: true }
);

const twMerge = inject("twMerge");
const computedWrapClass = computed(() => twMerge("divide-y dark:divide-zinc-700", props.wrapperClass));

provide("open", open);
provide("close", close);

defineExpose({
  open,
  close
});
</script>

<template>
  <component :is="is" v-bind="$attrs">
    <component :is="buttonIs" v-if="$slots.button" @click="open">
      <slot name="button" />
    </component>

    <Modal :max-width="maxWidth" dialog-class="p-0 overflow-hidden" :show="visible" @close="close">
      <div :class="computedWrapClass">
        <template v-for="(action, index) in actions" :key="index">
          <ActionButton v-if="action?.enable" @click="action.action">{{ action.label }}</ActionButton>
        </template>
        <!--end::buttons-->

        <slot :close="close" />
        <!--end::slot-->

        <ActionButton classes="bg-50 dark:bg-zinc-900 text-500 hover:text-900">취소</ActionButton>
        <!--end::button-->
      </div>
    </Modal>
  </component>
</template>
